/**
 * Utility functions for safe date handling and validation
 */

/**
 * Validates and creates a valid Date object from a timestamp string.
 * Returns current date if timestamp is invalid, null, undefined, or empty.
 *
 * @param timestamp - ISO string timestamp or null/undefined
 * @returns Valid Date object (current date as fallback)
 */
export const createValidDate = (timestamp: string | null | undefined): Date => {
  // Handle null, undefined, or empty strings
  if (!timestamp || typeof timestamp !== "string" || timestamp.trim() === "") {
    return new Date();
  }

  try {
    const date = new Date(timestamp);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return new Date();
    }
    return date;
  } catch {
    return new Date();
  }
};

/**
 * Safely converts a Date object to ISO string.
 * Returns current date ISO string if date is invalid, null, or undefined.
 *
 * @param date - Date object or null/undefined
 * @returns ISO string representation of the date (current date as fallback)
 */
export const toSafeISOString = (date: Date | null | undefined): string => {
  // Validate date exists and is a Date instance
  if (!date) {
    return new Date().toISOString();
  }

  // Double check it's a Date instance
  if (!(date instanceof Date)) {
    return new Date().toISOString();
  }

  // Check if date is valid
  const timeValue = date.getTime();
  if (isNaN(timeValue) || !isFinite(timeValue)) {
    return new Date().toISOString();
  }

  // Try to convert to ISO string
  try {
    const isoString = date.toISOString();
    // Validate the result is not empty
    if (!isoString || isoString.trim() === "") {
      return new Date().toISOString();
    }
    return isoString;
  } catch {
    return new Date().toISOString();
  }
};
